import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { Volume2, ArrowUpRight, Mic, Globe, Languages, Clock, Zap, Shield, Play, MessageSquare, Users, Sparkles, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Languages, title: 'Multi-language support', text: 'Convert text to natural-sounding audio in 30+ languages including English, Spanish, French, German, and more.' },
  { icon: Mic, title: 'Natural voices', text: 'Choose from dozens of human-like voices with different accents, ages, and speaking styles.' },
  { icon: Zap, title: 'Real-time conversion', text: 'Transform text to speech instantly with low latency, perfect for live customer interactions.' },
  { icon: Globe, title: 'Global accessibility', text: 'Make your content accessible to customers worldwide with localized audio in their native language.' },
];

const useCases = [
  { title: 'Automated responses', text: 'Provide instant audio responses for IVR systems, chatbots, and automated customer service.' },
  { title: 'Accessibility', text: 'Make your website and applications accessible to visually impaired users with audio content.' },
  { title: 'E-learning', text: 'Convert educational content into audio format for podcasts, audiobooks, and learning modules.' },
  { title: 'Broadcast announcements', text: 'Generate professional voice announcements for phone systems, public address, and emergency alerts.' },
];

const voiceExamples = [
  { language: 'English (US)', voice: 'Sarah', style: 'Professional, clear, friendly' },
  { language: 'English (UK)', voice: 'James', style: 'Formal, authoritative, calm' },
  { language: 'Spanish', voice: 'Maria', style: 'Warm, enthusiastic, natural' },
  { language: 'French', voice: 'Pierre', style: 'Elegant, sophisticated, smooth' },
  { language: 'German', voice: 'Hans', style: 'Precise, clear, confident' },
  { language: 'Portuguese', voice: 'Ana', style: 'Friendly, approachable, clear' },
];

const benefits = [
  { icon: Clock, title: 'Save time', description: 'Generate audio content instantly without hiring voice actors or recording studios.' },
  { icon: DollarSign, title: 'Cost-effective', description: 'Included free with Zuri Voice - no per-minute charges for text-to-speech conversion.' },
  { icon: Shield, title: 'Consistent quality', description: 'Maintain consistent brand voice across all customer touchpoints with standardized audio.' },
  { icon: Sparkles, title: 'Easy integration', description: 'Seamlessly integrate with IVR, chatbots, and other Zuri Voice features.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What languages and voices are available?',
    answer: 'Text-to-speech supports 30+ languages including English (US, UK, Australian), Spanish, French, German, Portuguese, Italian, Dutch, and more. Each language offers multiple voice options with different accents, ages, and speaking styles to match your brand personality.'
  },
  {
    question: 'Is text-to-speech included in the pricing?',
    answer: 'Yes! Text-to-speech is completely free with Zuri Voice. There are no per-minute charges or additional costs for converting text to speech. You only pay for the actual voice calls if you use phone numbers.'
  },
  {
    question: 'How do I integrate text-to-speech with my IVR?',
    answer: 'Text-to-speech integrates seamlessly with our drag-and-drop IVR builder. Simply add a text-to-speech node to your call flow, enter your text, select the voice and language, and it will automatically convert to audio when customers reach that point in the IVR.'
  },
  {
    question: 'Can I customize the voice and speaking style?',
    answer: 'Yes, you can choose from multiple voices per language and adjust parameters like speaking speed, pitch, and emphasis. This allows you to create a voice experience that matches your brand identity and customer preferences.'
  },
  {
    question: 'What is the audio quality like?',
    answer: 'Our text-to-speech uses advanced neural network technology to produce natural, human-like speech. The audio quality is broadcast-ready and suitable for professional IVR systems, customer service lines, and public announcements.'
  },
  {
    question: 'Can I use text-to-speech for recorded content?',
    answer: 'Absolutely. You can generate audio files for podcasts, audiobooks, training materials, website content, and more. The generated audio can be downloaded and used across multiple platforms and channels.'
  },
  {
    question: 'How fast is the text-to-speech conversion?',
    answer: 'Conversion happens in real-time with low latency, typically under 500ms for short phrases. This makes it perfect for live customer interactions where immediate audio response is needed.'
  },
  {
    question: 'Is there a limit on how much text I can convert?',
    answer: 'There are no limits on text-to-speech conversion volume. You can convert as much text as you need at no additional cost. This makes it ideal for businesses of all sizes.'
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function TextToSpeech() {
  return (
    <>
      <SEO
        title="Text-to-Speech - Natural Voice Conversion | Zuri Voice"
        description="Convert text into natural-sounding audio in 30+ languages and voices. Perfect for automated responses, IVR systems, and accessibility. Included free with Zuri Voice."
        path="/text-to-speech"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Volume2 className="h-4 w-4" /> Included free
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Text to speech that sounds <span className="gradient-text">human</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Convert text into natural-sounding audio in 30+ languages and voices. Perfect for automated responses, IVR systems, and accessibility. No extra cost.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/zuri-voice"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Back to Zuri Voice <Volume2 className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/A%20transcribe.png"
                  alt="Text-to-speech dashboard showing voice conversion and language selection"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful features, zero cost</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to create professional audio content.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="voices-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="voices-heading" className="section-title">Voices for every brand</h2>
            <p className="section-subtitle mx-auto mt-4">Choose from dozens of natural voices across multiple languages.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {voiceExamples.map((voice) => (
              <div key={voice.language} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-brand-600">
                    <Play className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{voice.voice}</h3>
                    <p className="text-sm text-slate-500">{voice.language}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600">{voice.style}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why use text-to-speech?</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform how you communicate.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="integration-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="integration-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">Seamless integration</h2>
              <p className="mt-4 text-slate-300">
                Text-to-speech works perfectly with other Zuri Voice features to create comprehensive voice solutions.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: MessageSquare,
                    title: 'IVR Systems',
                    description: 'Add text-to-speech nodes to your IVR flows for dynamic, personalized audio menus.',
                  },
                  {
                    icon: Users,
                    title: 'Chatbots',
                    description: 'Enable audio responses in AI-powered chatbots for multi-modal customer interactions.',
                  },
                  {
                    icon: Sparkles,
                    title: 'Automated Announcements',
                    description: 'Schedule and broadcast important messages with professional voice quality.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Getting started</h3>
              <ol className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-bold">1</span>
                  <span>Access text-to-speech from your Zuri Voice dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-bold">2</span>
                  <span>Enter your text and select language, voice, and style preferences</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-bold">3</span>
                  <span>Preview the audio and integrate with IVR or download as audio file</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about text-to-speech</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}